import { useMutation, useQuery } from '@tanstack/react-query';
import {
  USER_IMPORT_SFLAG_API_VALUE,
  type UserImportTemplate,
  type UserImportSflag,
} from '../constants/userImport';
import { PaymentStatus, User } from '../types';
import { apiClient } from '../lib/apiClient';

export type { UserImportTemplate, UserImportSflag } from '../constants/userImport';

type ApiUsersMeta = {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
};

export type UsersResponse = {
  data: User[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
};

export type UserImportStats = {
  failures: unknown[];
  imported: number;
  skipped: number;
  totalRows: number;
};

export type UserImportResponse = {
  users: User[];
  stats: UserImportStats;
};

type ApiUserPayload = {
  id?: number;
  merchantCustomerId?: string;
  firstName?: string;
  first_name?: string;
  lastName?: string;
  last_name?: string;
  phoneNumber?: string;
  phone_number?: string;
  email?: string;
  authorizeNetCustomerId?: string;
  authorize_net_customer_id?: string;
  paymentStatus?: string;
  payment_status?: string;
  createdAt?: string;
  updatedAt?: string;
};

const paymentStatusFallback: PaymentStatus = 'Pending';

const normalizePaymentStatus = (status?: string): PaymentStatus => {
  if (!status) return paymentStatusFallback;

  const normalizedStatus = status.toLowerCase();
  if (normalizedStatus === 'paid') return 'Paid';
  if (normalizedStatus === 'pending') return 'Pending';
  if (normalizedStatus === 'overdue') return 'Overdue';
  return paymentStatusFallback;
};

const mapApiUser = (item: ApiUserPayload, index: number): User => ({
  id: item.id ?? index + 1,
  firstName: item.firstName ?? item.first_name ?? 'Unknown',
  lastName: item.lastName ?? item.last_name ?? 'Unknown',
  phoneNumber: item.phoneNumber ?? item.phone_number ?? '--',
  email: item.email ?? 'unknown@example.com',
  authorizeNetCustomerId: item.authorizeNetCustomerId ?? item.authorize_net_customer_id ?? '--',
  paymentStatus: normalizePaymentStatus(item.paymentStatus ?? item.payment_status),
});

const extractUsersFromResponse = (payload: unknown): ApiUserPayload[] => {
  if (Array.isArray(payload)) return payload as ApiUserPayload[];

  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: ApiUserPayload[] }).data;
  }

  if (payload && typeof payload === 'object' && Array.isArray((payload as { users?: unknown }).users)) {
    return (payload as { users: ApiUserPayload[] }).users;
  }

  return [];
};

const extractMetaFromResponse = (payload: unknown, fallbackCount: number): UsersResponse['meta'] => {
  if (payload && typeof payload === 'object' && (payload as { meta?: unknown }).meta) {
    const rawMeta = (payload as { meta?: ApiUsersMeta }).meta ?? {};
    return {
      total: rawMeta.total ?? fallbackCount,
      page: rawMeta.page ?? 1,
      limit: rawMeta.limit ?? fallbackCount,
      totalPages: rawMeta.totalPages ?? 1,
    };
  }

  return {
    total: fallbackCount,
    page: 1,
    limit: fallbackCount,
    totalPages: 1,
  };
};

const extractUserImportStats = (payload: unknown, importedCount: number): UserImportStats => {
  if (!payload || typeof payload !== 'object') {
    return {
      failures: [],
      imported: importedCount,
      skipped: 0,
      totalRows: importedCount,
    };
  }

  const payloadWithStats = payload as Partial<UserImportStats>;
  const failures = Array.isArray(payloadWithStats.failures) ? payloadWithStats.failures : [];

  return {
    failures,
    imported: Number(payloadWithStats.imported ?? importedCount),
    skipped: Number(payloadWithStats.skipped ?? 0),
    totalRows: Number(payloadWithStats.totalRows ?? importedCount),
  };
};

const fetchUsers = async (
  page: number,
  limit: number,
  searchTerm: string,
  sortField: string,
  sortDirection: 'asc' | 'desc'
): Promise<UsersResponse> => {
  const normalizedSearchTerm = searchTerm.trim();
  const searchParam = normalizedSearchTerm.length > 0 ? `&search=${encodeURIComponent(normalizedSearchTerm)}` : '';
  const sortFieldParam = sortField.trim().length > 0 ? `&sortBy=${encodeURIComponent(sortField)}` : '';
  const sortDirectionParam = sortField.trim().length > 0 ? `&sortDirection=${encodeURIComponent(sortDirection)}` : '';
  const responseData = await apiClient.getJson<unknown>(
    `/users?page=${page}&limit=${limit}${searchParam}${sortFieldParam}${sortDirectionParam}`
  );
  const mappedUsers = extractUsersFromResponse(responseData).map(mapApiUser);
  const meta = extractMetaFromResponse(responseData, mappedUsers.length);

  return {
    data: mappedUsers,
    meta,
  };
};

type ImportUsersFromFileInput = {
  file: File;
  mode: UserImportSflag;
  template: UserImportTemplate | null;
};

const importUsersFromFile = async ({ file, mode, template }: ImportUsersFromFileInput): Promise<UserImportResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', USER_IMPORT_SFLAG_API_VALUE[mode]);
  if (template) {
    formData.append('template', template);
  }

  const responseData = await apiClient.postFormJson<unknown>('/users/import', formData, {
    timeoutMs: 120000,
  });
  const users = extractUsersFromResponse(responseData).map(mapApiUser);

  return {
    users,
    stats: extractUserImportStats(responseData, users.length),
  };
};

type UpdateUserInput = {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
};

const updateUser = async ({ id, firstName, lastName, phoneNumber }: UpdateUserInput): Promise<unknown> => {
  return apiClient.patchJson<unknown>(`/users/${id}`, {
    firstName,
    lastName,
    phoneNumber,
  });
};

const deleteUser = async (id: number): Promise<unknown> => {
  return apiClient.deleteJson<unknown>(`/users/${id}`);
};

const deleteAllNonAdminUsers = async (): Promise<unknown> => {
  return apiClient.deleteJson<unknown>('/users/non-admin');
};

export const useUsersQuery = (
  enabled: boolean,
  page: number,
  limit: number,
  searchTerm = '',
  sortField = '',
  sortDirection: 'asc' | 'desc' = 'asc'
) =>
  useQuery({
    queryKey: ['users', page, limit, searchTerm, sortField, sortDirection],
    queryFn: () => fetchUsers(page, limit, searchTerm, sortField, sortDirection),
    enabled,
    refetchOnMount: 'always',
  });

export const useImportUsersMutation = () =>
  useMutation({
    mutationFn: importUsersFromFile,
  });

export const useUpdateUserMutation = () =>
  useMutation({
    mutationFn: updateUser,
  });

export const useDeleteUserMutation = () =>
  useMutation({
    mutationFn: deleteUser,
  });

export const useDeleteAllUsersMutation = () =>
  useMutation({
    mutationFn: deleteAllNonAdminUsers,
  });
